// Shared registries, layout constants and the geometry-bucket helpers.
//
// Everything that dresses the shop pushes into the arrays declared here:
// COLLIDERS (player blocking rects), PICKABLES (raycast targets), RACKS (the
// diggable bins) and SPEAKER_ANCHORS. The counter placement set, the sleeve
// registry and the crate/run constants live here too.
//
// The bottom half is the bucket convention the whole codebase is built on:
// part()/partAt()/partL() push a transformed geometry into a plain array, and
// flushBucket() merges one array into a single Mesh. frameOf() builds the local
// frame that partL() transforms through.
//
// Recovered from the production bundle. Numbers and strings are byte-identical
// to the shipped source.

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { COUNTER_POS, BIN_RUNS_X } from './zones.js';
import { ROOM } from './room.js';
import { SPEAKERS } from './audio.js';

// ---------------------------------------------------------------------------
// Record dimensions and the global registries
// ---------------------------------------------------------------------------

const LP_SIZE = .315;

const LP_THICKNESS = .009;

const COLLIDERS = [];

const PICKABLES = [];

const RACKS = [];

// ---------------------------------------------------------------------------
// Counter / till placement
// ---------------------------------------------------------------------------

const COUNTER_SIZE = 2.3;

const COUNTER_RECT = {
    x0: -COUNTER_SIZE / 2,
    x1: COUNTER_SIZE / 2,
    z0: -1.35 - COUNTER_SIZE / 2,
    z1: -1.35 + COUNTER_SIZE / 2
};

const COUNTER_X = COUNTER_POS.x;

const COUNTER_Z = COUNTER_POS.z;

const COUNTER_YAW = 0;

const COUNTER = {
    position: new THREE.Vector3(COUNTER_X - .1, 1.12, COUNTER_Z),
    stand: new THREE.Vector3(COUNTER_X - .8, 0, COUNTER_Z),
    faceDir: new THREE.Vector3(1, 0, 0),
    yaw: COUNTER_YAW,
    radius: 1.15,
    label: "PAY HERE"
};

// ---------------------------------------------------------------------------
// Speakers
// ---------------------------------------------------------------------------

const SPEAKER_ANCHORS = [];

// Used when audio.js SPEAKERS is empty or not an array.
const SPEAKER_LAYOUT_FALLBACK = [{
    id: "main-l",
    role: "main",
    x: -2.6,
    y: 2.34,
    z: 5.95,
    aim: {
        x: .6,
        y: 1.5,
        z: 0
    }
}, {
    id: "main-r",
    role: "main",
    x: 2.6,
    y: 2.34,
    z: 5.95,
    aim: {
        x: -.6,
        y: 1.5,
        z: 0
    }
}, {
    id: "fill-l",
    role: "fill",
    x: -2.6,
    y: 2.34,
    z: -3.6,
    aim: {
        x: .6,
        y: 1.5,
        z: -3.6
    }
}, {
    id: "fill-r",
    role: "fill",
    x: 2.6,
    y: 2.34,
    z: -3.6,
    aim: {
        x: -.6,
        y: 1.5,
        z: -3.6
    }
}];

// Normalises whichever layout is in force into complete {id, role, x, y, z, aim}
// records, dropping any entry without a finite position.
function speakerRig() {
    const layout = (Array.isArray(SPEAKERS) && SPEAKERS.length) ? SPEAKERS : SPEAKER_LAYOUT_FALLBACK;
    return layout
        .filter(speaker => speaker && Number.isFinite(speaker.x) && Number.isFinite(speaker.y) && Number.isFinite(speaker.z))
        .map(speaker => ({
            id: speaker.id || "spk",
            role: speaker.role || "main",
            x: speaker.x,
            y: speaker.y,
            z: speaker.z,
            aim: (speaker.aim && Number.isFinite(speaker.aim.x)) ? speaker.aim : {
                x: 0,
                y: 1.5,
                z: speaker.z
            }
        }));
}

// ---------------------------------------------------------------------------
// Sleeves, crates and the bin runs
// ---------------------------------------------------------------------------

const SLEEVE_SIZE = .278;

const SLEEVE_PITCH = .0175;

const SLEEVES = {
    dummy: [],
    tile: [],
    tiles: null,
    mesh: null,
    last: null,
    active: null
};

const CRATE_DEPTH = .5;

const CRATE_WALL = .08;

const WIDE_ROOM = ROOM.w >= 5.2;

const RUN_X = WIDE_ROOM ? BIN_RUNS_X.slice() : [-.72, -.72, .72, .72];

// ---------------------------------------------------------------------------
// Collider and geometry-bucket helpers
// ---------------------------------------------------------------------------

function addCollider(x, z, width, depth, pad = .06) {
    COLLIDERS.push({
        minX: x - width / 2 - pad,
        maxX: x + width / 2 + pad,
        minZ: z - depth / 2 - pad,
        maxZ: z + depth / 2 + pad
    });
}

// World-space push: bake position + YXZ rotation into the geometry, then add it
// to the bucket array.
function part(bucket, geometry, x, y, z, rotX = 0, rotY = 0, rotZ = 0) {
    geometry.applyMatrix4(new THREE.Matrix4().compose(
        new THREE.Vector3(x, y, z),
        new THREE.Quaternion().setFromEuler(new THREE.Euler(rotX, rotY, rotZ, "YXZ")),
        new THREE.Vector3(1, 1, 1)
    ));
    bucket.push(geometry);
    return geometry;
}

// As part(), but taking a Vector3 position and a Quaternion directly.
function partAt(bucket, geometry, position, quaternion) {
    geometry.applyMatrix4(new THREE.Matrix4().compose(position, quaternion, new THREE.Vector3(1, 1, 1)));
    bucket.push(geometry);
    return geometry;
}

// Local-space push: place the geometry inside `frame` (a Matrix4 from frameOf),
// then transform it up into world space.
function partL(bucket, frame, geometry, x, y, z, rotX = 0, rotY = 0, rotZ = 0) {
    geometry.applyMatrix4(new THREE.Matrix4().compose(
        new THREE.Vector3(x, y, z),
        new THREE.Quaternion().setFromEuler(new THREE.Euler(rotX, rotY, rotZ, "YXZ")),
        new THREE.Vector3(1, 1, 1)
    ));
    geometry.applyMatrix4(frame);
    bucket.push(geometry);
    return geometry;
}

// Merge one bucket into a single Mesh and parent it. Returns null on an empty
// bucket or a failed merge.
function flushBucket(parent, geometries, material, castShadow = true) {
    if (!geometries.length) return null;
    const merged = mergeGeometries(geometries, false);
    if (!merged) return null;
    const mesh = new THREE.Mesh(merged, material);
    mesh.castShadow = castShadow;
    mesh.receiveShadow = true;
    parent.add(mesh);
    return mesh;
}

// Local frame at (x, z) on the floor, rotated by yaw about Y.
function frameOf(x, z, yaw) {
    return new THREE.Matrix4().compose(
        new THREE.Vector3(x, 0, z),
        new THREE.Quaternion().setFromEuler(new THREE.Euler(0, yaw, 0)),
        new THREE.Vector3(1, 1, 1)
    );
}

export {
    LP_SIZE,
    LP_THICKNESS,
    COLLIDERS,
    PICKABLES,
    RACKS,
    COUNTER_SIZE,
    COUNTER_RECT,
    COUNTER_X,
    COUNTER_Z,
    COUNTER_YAW,
    COUNTER,
    SPEAKER_ANCHORS,
    SPEAKER_LAYOUT_FALLBACK,
    speakerRig,
    SLEEVE_SIZE,
    SLEEVE_PITCH,
    SLEEVES,
    CRATE_DEPTH,
    CRATE_WALL,
    WIDE_ROOM,
    RUN_X,
    addCollider,
    part,
    partAt,
    partL,
    flushBucket,
    frameOf
};
